import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Detail as DetailComponent, InfoHeader, useRelationshipDiscoverer } from 'src/mtska-frontend-app-component';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { AppContext } from 'src/mtska-frontend-app-scaffolding';
import { CircularProgress } from '@mui/joy';
import { Backdrop } from "@mui/material"
import dayjs from 'dayjs'

const dataProviderName = 'calendars/carcalendar';
const model = 'calendars/carcalendar';
const wizardName = 'Calendar management';
const wizardObject = 'Calendar';
const wizardAction = 'calendarManagement';

const CarCalendarManagement = (params) => {
    const { t } = useTranslation();
    const { userCapabilities } = useContext(AppContext);
    const [drawable, setDrawable] = useState(false);

    const { doAction, item, loadOne, errorMessage, errors, saving, reset, success } = useDataProvider(dataProviderName, params.multipart);
    const { discover } = useRelationshipDiscoverer(params, item?.data, params?.detailStack?.inverseRelation, params?.detailStack?.stack);

    let detailConfig = {
        "tabs": [
            {
                "name": "management",
                "fieldsets": [
                    {
                        "name": "configuration",
                        "fields": {
                            "status": {
                                "type": "select",
                                "options": [
                                    { "label": "active", "value": "active" },
                                    { "label": "paused", "value": "paused" }
                                ],
                                "required": true,
                                "className": "col-8",
                                "placeholder": "select",
                                "icon": "faChartNetwork"
                            },
                            "bookabilityStart_at": {
                                "type": "date",
                                "required": true,
                                "className": "col-2",
                                "placeholder": "insert",
                                "icon": "faCalendar"
                            },
                            "bookabilityEnd_at": {
                                "type": "date",
                                "className": "col-2",
                                "placeholder": "insert",
                                "icon": "faCalendar"
                            },
                            "minimumDaysBookable": {
                                "type": "number",
                                "required": true,
                                "className": "col-3",
                                "placeholder": "insert",
                                "icon": "faHourglass"
                            },
                            "maximumDaysBookable": {
                                "type": "number",
                                "required": true,
                                "className": "col-3",
                                "placeholder": "insert",
                                "icon": "faHourglass"
                            },
                            "recoveryTimeAtReenter": {
                                "type": "number",
                                "required": true,
                                "className": "col-3",
                                "placeholder": "insert",
                                "icon": "faHourglass"
                            },
                            "requiredStartDay": {
                                "type": "select",
                                "options": [
                                    { label: ('sunday'), value: 0 },
                                    { label: ('monday'), value: 1 },
                                    { label: ('tuesday'), value: 2 },
                                    { label: ('wednesday'), value: 3 },
                                    { label: ('thursday'), value: 4 },
                                    { label: ('friday'), value: 5 },
                                    { label: ('saturday'), value: 6 },
                                    { label: ('no_restriction'), value: null },
                                ],
                                "required": true,
                                "className": "col-3",
                                "placeholder": "select",
                                "icon": "faCalendarDay"
                            },
                            "timeframePickup": {
                                "type": "text",
                                "className": "col-6",
                                "placeholder": "insert",
                                "icon": "faClock"
                            },
                            "timeframeReenter": {
                                "type": "text",
                                "className": "col-6",
                                "placeholder": "insert",
                                "icon": "faClock"
                            },
                            "customMessage": {
                                "type": "textarea",
                                "className": "col-12",
                                "minrows": 3,
                                "placeholder": "insert",
                                "icon": "faInputText"
                            },
                        }
                    }
                ]
            }
        ],
        "validation": []
    }

    if (item?.data && item?.data?._id != undefined && item.data._id == params.car?.calendar?._id) {
        detailConfig.tabs[0].fieldsets.push(
            {
                name: "booking",
                fields: {
                    calendar: {
                        type: "calendarBooking",
                        calendar_id: item.data._id ?? null,
                        className: "col-12"
                    }
                }
            }
        )
    }

    useEffect(() => {
        reset();
        if (params.car?.calendar?._id) {
            loadOne(params.car?.calendar?._id).then(e => setDrawable(true));
        } else {
            loadOne(null).then(e => setDrawable(true));
        }
    }, []);

    const [editing, setEditing] = useState(false)

    useEffect(() => {
        if (success && editing) {
            (async () => {
                try {
                    await params.handleClose(params.uid, item.data)
                } catch (e) { }
            })()
        }
    }, [success])

    const handleSave = async (data) => {
        if (dayjs.isDayjs(data.bookabilityStart_at)) {
            data.bookabilityStart_at = data.bookabilityStart_at.format('YYYY-MM-DDTHH:mm:ss')
        }

        if (dayjs.isDayjs(data.bookabilityEnd_at)) {
            data.bookabilityEnd_at = data.bookabilityEnd_at.format('YYYY-MM-DDTHH:mm:ss')
        }
        doAction && doAction({ action: wizardAction, model: model ? model : dataProviderName, data: data });
    }

    const car = discover('car');
    return (
        !drawable ? <Backdrop open={!drawable} style={{ backgroundColor: "white" }}><CircularProgress /></Backdrop> :
            <>
                <DetailComponent
                    values={{
                        "car_id": car?._id,
                        "legal_entity_id": car?.legalEntity?._id,
                        "_id": item?.data?._id,
                        "status": item?.data?.status,
                        "bookabilityStart_at": item?.data?.bookabilityStart_at,
                        "bookabilityEnd_at": item?.data?.bookabilityEnd_at,
                        "minimumDaysBookable": item?.data?.minimumDaysBookable,
                        "maximumDaysBookable": item?.data?.maximumDaysBookable,
                        "recoveryTimeAtReenter": item?.data?.recoveryTimeAtReenter,
                        "requiredStartDay": item?.data?.requiredStartDay,
                        "timeframePickup": item?.data?.timeframePickup,
                        "timeframeReenter": item?.data?.timeframeReenter,
                        "customMessage": item?.data?.customMessage,
                    }}
                    modelName={model.split('/')[1]}
                    detailConfig={detailConfig}
                    dataProviderName={dataProviderName}
                    onEditing={setEditing}
                    save={handleSave}
                    saving={saving}
                    errors={errors}
                    errorMessage={errorMessage}
                    title={t(wizardName)}
                    canSave={userCapabilities && userCapabilities[dataProviderName + '.' + wizardAction.toLowerCase()]}
                    header={
                        <InfoHeader
                            view="wizard"
                            title={t(wizardName)}
                            subtitle={t('Define a {{wizardObject}} to be associated to {{carIdentifier}}', { wizardObject: wizardObject, carIdentifier: (car?.plate ? car?.plate : car?.setup?.make + " " + car?.setup?.model) })}
                        />
                    }
                    {...params}
                />
            </>
    )
}

export default CarCalendarManagement;


